import {z} from 'zod';

import {
  IsoUtcDateTimeSchema,
  MediaFormatSchema,
  MetricNameSchema,
  UuidV4Schema,
} from './common.js';
import type {MediaFormat, MetricName} from './common.js';
import {CtaObjectiveSchema} from './editorial.js';
import {AutonomyLevelValueSchema} from './panel.js';

/**
 * Contratos de métrica, atribuição comercial e autonomia.
 *
 * Quatro regras atravessam o arquivo inteiro, e nenhuma delas é preferência de
 * estilo:
 *
 * 1. **Indisponível nunca é zero.** Toda medição é união discriminada por
 *    `availability`, e o ramo `unavailable` obriga `value: null` mais um
 *    `reasonCode`. Uma operação parada com métrica zerada passa a semana
 *    parecendo saudável, e é exatamente esse o erro que o schema torna
 *    impossível de representar.
 * 2. **Número sem rastro não se defende.** Contagem de funil e valor comercial
 *    carregam `trace`, com os identificadores dos eventos que os formaram.
 *    Receita que ninguém consegue reconstituir é receita que ninguém sustenta
 *    numa conversa com o contador.
 * 3. **Gasto de campanha é lançamento humano.** `spendSource` é literal
 *    `manual_entry` porque não existe Marketing API no MVP: o número foi
 *    copiado do Ads Manager por uma pessoa, e o dado precisa dizer isso para
 *    ninguém confundir com medição automática.
 * 4. **Autonomia não sobe sozinha.** A avaliação recomenda; quem eleva é o
 *    dono, com reautenticação e motivo escrito. Por isso `AutonomyEvaluation` e
 *    `AutonomyState` são estruturas separadas, e a primeira não tem nenhum
 *    campo capaz de mudar a segunda.
 *
 * Nomes que já existem em `panel.ts` (`MetricValueSchema`, `CandidateSchema`,
 * `AutonomyLevelSchema`) NÃO se repetem aqui: os dois arquivos saem pelo mesmo
 * `index.ts`, e nome repetido em reexportação estrela some sem erro claro.
 */

// ---------------------------------------------------------------------------
// Vocabulário de métrica
// ---------------------------------------------------------------------------

export const MetricScopeSchema = z.enum(['account', 'media']);
export type MetricScope = z.infer<typeof MetricScopeSchema>;

/**
 * Métricas que NÃO EXISTEM para um formato, e por quê.
 *
 * É a única lista deste arquivo que faz uma afirmação sobre a Meta, então ela
 * só carrega o que é impossível pela natureza do formato, e nunca o que é
 * apenas duvidoso. Métrica que talvez exista e que o plano de consulta não pede
 * sai com `not_requested`, que diz a verdade sem afirmar nada sobre o
 * fornecedor.
 *
 * - `watchTimeMs` e `avgWatchTimeMs` saem de `ig_reels_video_view_total_time` e
 *   `ig_reels_avg_watch_time`, que são métricas de REELS. Pedi-las em outro
 *   formato é o defeito que o F13 encontrou.
 * - `saved` não existe em Story: não há ação de salvar um Story.
 * - `videoDurationMs` não existe em foto.
 *
 * `shares` em Story ficou de fora desta lista de propósito: pode existir, e a
 * casa não tem como provar sem chamar a Meta. Ela não é pedida, então sai como
 * `not_requested`, e a tela diz que a coleta não pediu.
 */
export const METRICS_IMPOSSIBLE_BY_FORMAT: Readonly<
  Record<MediaFormat, readonly MetricName[]>
> = {
  reel: [],
  story: ['saved', 'watchTimeMs', 'avgWatchTimeMs'],
  photo: ['watchTimeMs', 'avgWatchTimeMs', 'videoDurationMs'],
  carousel: ['watchTimeMs', 'avgWatchTimeMs'],
};

/**
 * Que métrica existe em que escopo.
 *
 * Pedir `saved` para a conta ou `followers` para uma mídia não é dado faltando,
 * é chamada errada: a resposta viria vazia e viraria "indisponível" para
 * sempre, escondendo o defeito de quem chamou atrás de um estado legítimo.
 */
export const METRIC_SCOPE_ALLOWLIST: Readonly<
  Record<MetricScope, readonly MetricName[]>
> = {
  account: ['reach', 'profileVisits', 'followers'],
  media: [
    'reach',
    'views',
    'saved',
    'shares',
    'watchTimeMs',
    'avgWatchTimeMs',
    'videoDurationMs',
  ],
};

/** Métricas que são contagem inteira e não negativa. */
export const COUNT_METRICS: readonly MetricName[] = [
  'reach',
  'views',
  'profileVisits',
  'followers',
  'saved',
  'shares',
];

/**
 * Por que a medição não existe.
 *
 * Cada motivo aponta para uma ação diferente de quem lê o painel: `field_omitted`
 * é a Meta atrasando, `permission_denied` é escopo para reconectar,
 * `window_incomplete` é esperar, `not_applicable` é a métrica nunca ter existido
 * para aquele formato. Um único código genérico faria as quatro situações
 * parecerem a mesma, e a resposta a cada uma é distinta.
 */
export const MetricUnavailableReasonSchema = z.enum([
  /** A resposta não trouxe a métrica pedida. */
  'field_omitted',
  /** A métrica veio, sem valor para a janela. */
  'value_not_returned',
  /** Veio texto, nulo ou algo que não é número finito. */
  'value_not_numeric',
  /** Número impossível para a métrica, como contagem negativa. */
  'value_out_of_range',
  /** A mesma métrica voltou duas vezes com valores diferentes. */
  'duplicate_remote_entry',
  /** A janela pedida ainda não fechou, e o total ainda sobe. */
  'window_incomplete',
  /** A métrica não existe para este formato de mídia. */
  'not_applicable',
  /**
   * Esta coleta não PEDIU a métrica neste formato.
   *
   * Separado de `field_omitted` porque a ação é outra e o culpado é outro:
   * `field_omitted` é a Meta não ter trazido o que se pediu, e espera-se;
   * `not_requested` é decisão da casa, e se resolve mudando o plano de
   * consulta. Chamar a segunda de primeira é acusar o fornecedor de uma
   * escolha nossa, e deixaria a métrica parada para sempre esperando a Meta.
   */
  'not_requested',
  /** Faltou escopo no token para ler a métrica. */
  'permission_denied',
  /** A chamada falhou e não há resposta para normalizar. */
  'collection_failed',
]);
export type MetricUnavailableReason = z.infer<
  typeof MetricUnavailableReasonSchema
>;

/**
 * Uma medição com instante.
 *
 * O ramo `unavailable` não tem `value` numérico NENHUM. Não é `value: 0` com
 * bandeira ao lado: quem escrever `{availability: 'unavailable', value: 0}` é
 * recusado no parse, e não corrigido depois por disciplina de quem lê.
 */
export const MeasuredMetricSchema = z.discriminatedUnion('availability', [
  z.strictObject({
    availability: z.literal('available'),
    value: z.number().finite(),
    measuredAt: IsoUtcDateTimeSchema,
  }),
  z.strictObject({
    availability: z.literal('unavailable'),
    value: z.null(),
    reasonCode: MetricUnavailableReasonSchema,
    measuredAt: IsoUtcDateTimeSchema,
  }),
]);
export type MeasuredMetricDto = z.infer<typeof MeasuredMetricSchema>;

/** Janela da coleta. `lifetime` é o acumulado da mídia, sem recorte. */
export const MetricWindowSchema = z
  .strictObject({
    kind: z.enum(['closed_period', 'lifetime']),
    startsAt: IsoUtcDateTimeSchema,
    endsAt: IsoUtcDateTimeSchema,
  })
  .refine((window) => window.endsAt > window.startsAt, {
    message: 'a janela precisa terminar depois de começar',
    path: ['endsAt'],
  });
export type MetricWindowDto = z.infer<typeof MetricWindowSchema>;

/**
 * Proveniência de um snapshot: nome remoto, versão da API e impressão digital
 * da resposta já sanitizada.
 *
 * O hash existe para responder "de que resposta saiu este número?" meses
 * depois, sem guardar o corpo, que traz identificador de conta e de mídia.
 */
const snapshotIdentityShape = {
  accountId: UuidV4Schema,
  scope: MetricScopeSchema,
  /** Nulo em métrica de conta. Preenchido em métrica de mídia. */
  remoteMediaId: z.string().min(1).nullable(),
  /**
   * O formato da peça medida. Nulo em métrica de conta: a conta não é peça.
   *
   * Ele fica gravado no snapshot porque é o que escolheu o plano de consulta.
   * Sem ele, a pergunta "por que esta Story não tem `saved`?" só teria resposta
   * refazendo, meses depois, o raciocínio de quem escreveu o coletor.
   */
  mediaFormat: MediaFormatSchema.nullable(),
  metric: MetricNameSchema,
  remoteName: z.string().min(1),
  window: MetricWindowSchema,
  /** Versão da Graph API que respondeu, no formato `v23.0`. */
  sourceRevision: z.string().regex(/^v[0-9]+\.[0-9]+$/u),
  payloadSha256: z.string().regex(/^[0-9a-f]{64}$/u),
  measuredAt: IsoUtcDateTimeSchema,
} as const;

export const MetricSnapshotSchema = z
  .discriminatedUnion('availability', [
    z.strictObject({
      ...snapshotIdentityShape,
      availability: z.literal('available'),
      value: z.number().finite(),
      reasonCode: z.null(),
    }),
    z.strictObject({
      ...snapshotIdentityShape,
      availability: z.literal('unavailable'),
      value: z.null(),
      reasonCode: MetricUnavailableReasonSchema,
    }),
  ])
  .refine(
    (snapshot) =>
      snapshot.scope === 'media'
        ? snapshot.remoteMediaId !== null
        : snapshot.remoteMediaId === null,
    {
      message:
        'métrica de mídia exige remoteMediaId, e métrica de conta o exige nulo',
      path: ['remoteMediaId'],
    },
  )
  .refine(
    (snapshot) =>
      snapshot.scope === 'media'
        ? snapshot.mediaFormat !== null
        : snapshot.mediaFormat === null,
    {
      message:
        'métrica de mídia exige mediaFormat, e métrica de conta o exige nulo',
      path: ['mediaFormat'],
    },
  );
export type MetricSnapshotDto = z.infer<typeof MetricSnapshotSchema>;

// ---------------------------------------------------------------------------
// Rastro: todo número diz de onde veio
// ---------------------------------------------------------------------------

export const MetricTraceSchema = z.strictObject({
  kind: z.enum(['metric_snapshot', 'funnel_event', 'commercial_event']),
  /** Identificadores que formaram o número. Vazio quando indisponível. */
  ids: z.array(UuidV4Schema),
});
export type MetricTraceDto = z.infer<typeof MetricTraceSchema>;

export const AuditedMetricSchema = z.discriminatedUnion('availability', [
  z.strictObject({
    availability: z.literal('available'),
    value: z.number().finite(),
    measuredAt: IsoUtcDateTimeSchema,
    trace: MetricTraceSchema,
  }),
  z.strictObject({
    availability: z.literal('unavailable'),
    value: z.null(),
    reasonCode: MetricUnavailableReasonSchema,
    measuredAt: IsoUtcDateTimeSchema,
    trace: MetricTraceSchema,
  }),
]);
export type AuditedMetricDto = z.infer<typeof AuditedMetricSchema>;

/** Dinheiro é inteiro em centavos. Real com casa decimal em float acumula erro. */
export const AuditedMoneySchema = z.discriminatedUnion('availability', [
  z.strictObject({
    availability: z.literal('available'),
    currency: z.literal('BRL'),
    cents: z.int(),
    measuredAt: IsoUtcDateTimeSchema,
    trace: MetricTraceSchema,
  }),
  z.strictObject({
    availability: z.literal('unavailable'),
    currency: z.literal('BRL'),
    cents: z.null(),
    reasonCode: MetricUnavailableReasonSchema,
    measuredAt: IsoUtcDateTimeSchema,
    trace: MetricTraceSchema,
  }),
]);
export type AuditedMoneyDto = z.infer<typeof AuditedMoneySchema>;

// ---------------------------------------------------------------------------
// Funil e atribuição
// ---------------------------------------------------------------------------

/** As etapas do funil, da audiência até a venda, na ordem em que acontecem. */
export const FUNNEL_STAGES = [
  'mapaStarted',
  'diagnosisCompleted',
  'whatsappConsented',
  'whatsappOpened',
  'meeting',
  'proposal',
  'contract',
  'revenue',
] as const;

export const FunnelStageSchema = z.enum(FUNNEL_STAGES);
export type FunnelStage = z.infer<typeof FunnelStageSchema>;

/**
 * Força do vínculo entre um evento comercial e o conteúdo.
 *
 * `direct` exige código do Instagram resgatado e evento dentro da janela.
 * `assisted` é influência reconhecida sem vínculo que se sustente.
 * `channel_only` é o clique da bio: atribui campanha, nunca pessoa nem Reel.
 * `unattributed` é o que não tem origem conhecida, e continua contando como
 * venda, só não como venda do Instagram.
 */
export const AttributionKindSchema = z.enum([
  'direct',
  'assisted',
  'channel_only',
  'unattributed',
]);
export type AttributionKind = z.infer<typeof AttributionKindSchema>;

export const CommercialEventKindSchema = z.enum([
  'meeting',
  'proposal',
  'contract',
  'revenue',
]);
export type CommercialEventKind = z.infer<typeof CommercialEventKindSchema>;

/**
 * Origem declarada no lançamento.
 *
 * É união discriminada, e não três campos opcionais, porque "conversa e código
 * que divergem" simplesmente não tem como ser escrito. Três campos opcionais
 * exigiriam uma checagem cruzada que alguém precisa lembrar de fazer.
 */
export const CommercialEventAttributionSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('conversation'),
    conversationId: UuidV4Schema,
  }),
  z.strictObject({
    kind: z.literal('attribution_code'),
    attributionCodeId: UuidV4Schema,
  }),
  z.strictObject({kind: z.literal('channel'), channel: z.literal('instagram')}),
  z.strictObject({kind: z.literal('none')}),
]);
export type CommercialEventAttribution = z.infer<
  typeof CommercialEventAttributionSchema
>;

/**
 * Lançamento manual de evento comercial.
 *
 * `evidenceNote` é obrigatória e o objeto de evidência é opcional: reunião e
 * proposta nem sempre geram arquivo, mas nenhuma delas pode entrar sem alguém
 * escrever de onde tirou o dado. Receita exige valor positivo, porque receita
 * de zero centavo é lançamento errado, não venda pequena.
 */
export const CreateCommercialEventRequestSchema = z
  .strictObject({
    idempotencyKey: UuidV4Schema,
    kind: CommercialEventKindSchema,
    occurredAt: IsoUtcDateTimeSchema,
    attribution: CommercialEventAttributionSchema,
    amountCents: z.int().nullable(),
    currency: z.literal('BRL').nullable(),
    evidenceNote: z.string().trim().min(10).max(1_000),
    /** Id opaco emitido pelo armazenamento interno. Nunca chave de bucket. */
    evidenceObjectId: UuidV4Schema.nullable(),
    /** Correção é evento novo apontando para o antigo, nunca alteração. */
    reversesEventId: UuidV4Schema.nullable(),
  })
  .refine(
    (event) =>
      event.kind !== 'revenue' ||
      (event.amountCents !== null &&
        event.amountCents > 0 &&
        event.currency === 'BRL'),
    {
      message: 'receita exige amountCents positivo e currency BRL',
      path: ['amountCents'],
    },
  )
  .refine(
    (event) =>
      event.kind === 'revenue' ||
      (event.amountCents === null && event.currency === null),
    {
      message: 'evento que não é receita não carrega valor',
      path: ['amountCents'],
    },
  );
export type CreateCommercialEventRequest = z.infer<
  typeof CreateCommercialEventRequestSchema
>;

export const CommercialEventSchema = z.strictObject({
  id: UuidV4Schema,
  kind: CommercialEventKindSchema,
  occurredAt: IsoUtcDateTimeSchema,
  attributionKind: AttributionKindSchema,
  amountCents: z.int().nullable(),
  currency: z.literal('BRL').nullable(),
  /** Preenchido só quando o vínculo chega até a versão publicada. */
  contentVersionId: UuidV4Schema.nullable(),
  reversesEventId: UuidV4Schema.nullable(),
  createdAt: IsoUtcDateTimeSchema,
});
export type CommercialEventDto = z.infer<typeof CommercialEventSchema>;

// ---------------------------------------------------------------------------
// Visão geral
// ---------------------------------------------------------------------------

/**
 * Atualidade da leitura.
 *
 * `missing` não é sinônimo de zero: é a coleta que não aconteceu. A tela
 * precisa dos três estados para dizer "não sei" em vez de desenhar um gráfico
 * chão de zeros que parece operação parada mas saudável.
 */
export const DataFreshnessSchema = z.enum(['fresh', 'stale', 'missing']);
export type DataFreshness = z.infer<typeof DataFreshnessSchema>;

export const MetricsOverviewSchema = z.strictObject({
  accountId: UuidV4Schema,
  from: IsoUtcDateTimeSchema,
  to: IsoUtcDateTimeSchema,
  dataFreshness: DataFreshnessSchema,
  lastCollectedAt: IsoUtcDateTimeSchema.nullable(),
  audience: z.strictObject({
    reach: AuditedMetricSchema,
    followers: AuditedMetricSchema,
    profileVisits: AuditedMetricSchema,
  }),
  funnel: z.strictObject({
    mapaStarted: AuditedMetricSchema,
    diagnosisCompleted: AuditedMetricSchema,
    whatsappConsented: AuditedMetricSchema,
    whatsappOpened: AuditedMetricSchema,
  }),
  commercial: z.strictObject({
    meetings: AuditedMetricSchema,
    proposals: AuditedMetricSchema,
    contracts: AuditedMetricSchema,
    /**
     * Direta e assistida são campos distintos de propósito: somar as duas num
     * número só transformaria influência em venda atribuída, que é a conta que
     * ninguém consegue defender depois.
     */
    directRevenue: AuditedMoneySchema,
    assistedRevenue: AuditedMoneySchema,
  }),
  /** O que faltou, com nome. Lacuna anônima ninguém investiga. */
  missingDimensions: z.array(z.string().min(1)),
});
export type MetricsOverviewDto = z.infer<typeof MetricsOverviewSchema>;

// ---------------------------------------------------------------------------
// Score por coorte
// ---------------------------------------------------------------------------

export const SCORE_DIMENSIONS = [
  'retention',
  'saveRate',
  'shareRate',
  'ctaActionRate',
] as const;

export const ScoreDimensionNameSchema = z.enum(SCORE_DIMENSIONS);
export type ScoreDimensionName = z.infer<typeof ScoreDimensionNameSchema>;

/**
 * Pesos aprovados do ranking, em pontos percentuais.
 *
 * Somam 100. Dimensão indisponível não vira zero: o peso dela é redistribuído
 * proporcionalmente entre as disponíveis, e o `appliedWeight` aparece no
 * relatório para o leitor saber que aquela peça foi julgada por menos critérios.
 */
export const CONFIGURED_SCORE_WEIGHTS: Readonly<
  Record<ScoreDimensionName, number>
> = {
  retention: 35,
  saveRate: 25,
  shareRate: 20,
  ctaActionRate: 20,
};

/**
 * Por que uma dimensão do score não pôde ser usada.
 *
 * Além dos motivos de medição, existe `insufficient_cohort`: o valor foi medido,
 * mas não há gente suficiente do mesmo objetivo para dizer se ele é bom ou ruim.
 * Percentil de amostra pequena é número com aparência de estatística, e é
 * melhor não ter número do que ter um que ninguém pode usar.
 */
export const ScoreDimensionReasonSchema = z.union([
  MetricUnavailableReasonSchema,
  z.literal('insufficient_cohort'),
]);
export type ScoreDimensionReason = z.infer<typeof ScoreDimensionReasonSchema>;

export const ScoreDimensionSchema = z.strictObject({
  name: ScoreDimensionNameSchema,
  rawValue: z.number().finite().nullable(),
  percentile: z.number().min(0).max(100).nullable(),
  configuredWeight: z.number().min(0).max(100),
  appliedWeight: z.number().min(0).max(100),
  availability: z.enum(['available', 'unavailable']),
  reasonCode: ScoreDimensionReasonSchema.nullable(),
});
export type ScoreDimensionDto = z.infer<typeof ScoreDimensionSchema>;

export const ContentMetricsSchema = z.strictObject({
  contentVersionId: UuidV4Schema,
  objective: CtaObjectiveSchema,
  publishedAt: IsoUtcDateTimeSchema,
  /** Instante em que a peça completa 72 horas e passa a poder ser comparada. */
  eligibleAt: IsoUtcDateTimeSchema,
  metrics: z.strictObject({
    retention: MeasuredMetricSchema,
    saveRate: MeasuredMetricSchema,
    shareRate: MeasuredMetricSchema,
    ctaActionRate: MeasuredMetricSchema,
  }),
  dimensions: z.array(ScoreDimensionSchema).length(SCORE_DIMENSIONS.length),
  /** Nulo quando nenhuma dimensão está disponível. Nunca 0. */
  score: z.number().min(0).max(100).nullable(),
});
export type ContentMetricsDto = z.infer<typeof ContentMetricsSchema>;

// ---------------------------------------------------------------------------
// Candidatos a mídia paga
// ---------------------------------------------------------------------------

export const PaidCandidateStateSchema = z.enum([
  'recommended',
  'accepted',
  'rejected',
]);
export type PaidCandidateState = z.infer<typeof PaidCandidateStateSchema>;

export const PaidCandidateSchema = z
  .strictObject({
    id: UuidV4Schema,
    cycleId: UuidV4Schema,
    contentVersionId: UuidV4Schema,
    score: z.number().min(0).max(100),
    percentile: z.number().min(0).max(100),
    state: PaidCandidateStateSchema,
    decidedAt: IsoUtcDateTimeSchema.nullable(),
    decidedBy: UuidV4Schema.nullable(),
    reason: z.string().min(1).nullable(),
  })
  .refine(
    (candidate) =>
      candidate.state === 'recommended'
        ? candidate.decidedAt === null && candidate.decidedBy === null
        : candidate.decidedAt !== null && candidate.decidedBy !== null,
    {
      message: 'candidato decidido exige quem decidiu e quando',
      path: ['decidedBy'],
    },
  );
export type PaidCandidateDto = z.infer<typeof PaidCandidateSchema>;

/**
 * Aceitar e rejeitar carregam `expectedState`.
 *
 * Duas abas abertas na mesma recomendação decidem em ordens diferentes; sem o
 * estado esperado, a segunda decisão sobrescreveria a primeira em silêncio.
 */
export const AcceptPaidCandidateRequestSchema = z.strictObject({
  expectedState: z.literal('recommended'),
  reason: z.string().trim().min(10).max(1_000),
});
export type AcceptPaidCandidateRequest = z.infer<
  typeof AcceptPaidCandidateRequestSchema
>;

export const RejectPaidCandidateRequestSchema = z.strictObject({
  expectedState: z.literal('recommended'),
  reason: z.string().trim().min(10).max(1_000),
});
export type RejectPaidCandidateRequest = z.infer<
  typeof RejectPaidCandidateRequestSchema
>;

// ---------------------------------------------------------------------------
// Campanha manual: sem Marketing API
// ---------------------------------------------------------------------------

/**
 * Registro de campanha.
 *
 * `spendSource` é literal por um motivo prático: daqui a seis meses alguém vai
 * olhar um número de gasto e perguntar se veio da API. Um campo que só pode
 * valer `manual_entry` responde antes da pergunta, e o dia em que existir
 * medição automática o schema muda e o histórico continua legível.
 */
export const CreateCampaignRecordRequestSchema = z
  .strictObject({
    candidateId: UuidV4Schema,
    /** Identificador copiado do Ads Manager, sem formato garantido pela Meta. */
    adsManagerId: z.string().trim().min(1).max(120),
    startsAt: IsoUtcDateTimeSchema,
    endsAt: IsoUtcDateTimeSchema,
    spendCents: z.int().nonnegative(),
    currency: z.literal('BRL'),
    spendSource: z.literal('manual_entry'),
    objective: z.string().trim().min(1).max(200),
    result: z.string().trim().min(1).max(1_000),
    /** Id opaco do armazenamento interno, com hash, tamanho e MIME já inspecionados. */
    evidenceObjectId: UuidV4Schema,
  })
  .refine((campaign) => campaign.endsAt > campaign.startsAt, {
    message: 'a campanha precisa terminar depois de começar',
    path: ['endsAt'],
  });
export type CreateCampaignRecordRequest = z.infer<
  typeof CreateCampaignRecordRequestSchema
>;

export const CampaignRecordSchema = z.strictObject({
  id: UuidV4Schema,
  candidateId: UuidV4Schema,
  adsManagerId: z.string().min(1),
  startsAt: IsoUtcDateTimeSchema,
  endsAt: IsoUtcDateTimeSchema,
  spendCents: z.int().nonnegative(),
  currency: z.literal('BRL'),
  spendSource: z.literal('manual_entry'),
  objective: z.string().min(1),
  result: z.string().min(1),
  evidenceObjectId: UuidV4Schema,
  createdAt: IsoUtcDateTimeSchema,
  /** Quem digitou. Gasto lançado à mão tem dono com nome. */
  createdBy: UuidV4Schema,
});
export type CampaignRecordDto = z.infer<typeof CampaignRecordSchema>;

// ---------------------------------------------------------------------------
// Autonomia
// ---------------------------------------------------------------------------

/**
 * Modo efetivo.
 *
 * `paused` não é um nível: é a conta parada por causa operacional (token,
 * incidente, limite, leitura indisponível). O nível configurado continua onde
 * estava, e volta a valer quando a causa sai. Guardar pausa como "nível 0"
 * perderia a informação de qual nível o dono escolheu.
 */
export const AutonomyEffectiveModeSchema = z.enum([
  'paused',
  'level_0',
  'level_1',
  'level_2',
]);
export type AutonomyEffectiveMode = z.infer<typeof AutonomyEffectiveModeSchema>;

export const AutonomyRecommendationSchema = z.enum([
  'stay',
  'level_1',
  'level_2',
  'reduce_to_level_0',
  'pause',
]);
export type AutonomyRecommendation = z.infer<
  typeof AutonomyRecommendationSchema
>;

/**
 * Um critério medido.
 *
 * `measured` é anulável porque critério sem dado é critério NÃO atingido, e não
 * critério zerado: "zero violações" e "não sei quantas violações houve" levam a
 * decisões opostas sobre subir o nível.
 */
export const AutonomyCriterionSchema = z.strictObject({
  code: z.string().min(1),
  passed: z.boolean(),
  measured: z.union([z.number().finite(), z.string().min(1), z.null()]),
  required: z.union([z.number().finite(), z.string().min(1)]),
});
export type AutonomyCriterionDto = z.infer<typeof AutonomyCriterionSchema>;

export const AutonomyEvaluationSchema = z
  .strictObject({
    id: UuidV4Schema,
    accountId: UuidV4Schema,
    windowStartedAt: IsoUtcDateTimeSchema,
    windowEndedAt: IsoUtcDateTimeSchema,
    eligiblePieces: z.int().nonnegative(),
    /** Taxa entre 0 e 1. Nula quando o denominador não é conhecido. */
    successRate: z.number().min(0).max(1).nullable(),
    recommendation: AutonomyRecommendationSchema,
    criteria: z.array(AutonomyCriterionSchema).min(1),
    createdAt: IsoUtcDateTimeSchema,
  })
  .refine(
    (evaluation) =>
      evaluation.recommendation !== 'level_1' &&
      evaluation.recommendation !== 'level_2'
        ? true
        : evaluation.criteria.every((criterion) => criterion.passed),
    {
      message:
        'recomendar elevação exige todos os critérios atingidos na avaliação',
      path: ['recommendation'],
    },
  );
export type AutonomyEvaluationDto = z.infer<typeof AutonomyEvaluationSchema>;

/**
 * Estado da autonomia.
 *
 * `configuredLevel` é escolha do dono e `effectiveMode` é o que a operação
 * consegue sustentar agora. São campos distintos na resposta e no banco porque
 * juntá-los apagaria a diferença entre "o dono baixou" e "o sistema conteve".
 */
export const AutonomyStateSchema = z.strictObject({
  accountId: UuidV4Schema,
  configuredLevel: AutonomyLevelValueSchema,
  effectiveMode: AutonomyEffectiveModeSchema,
  reasonCode: z.string().min(1),
  policyVersionId: UuidV4Schema,
  changedAt: IsoUtcDateTimeSchema,
  /** Nulo só no estado inicial semeado pela migração. */
  changedBy: UuidV4Schema.nullable(),
});
export type AutonomyStateDto = z.infer<typeof AutonomyStateSchema>;

/**
 * Pedido de mudança de nível.
 *
 * `recommendationId` é anulável porque REDUZIR não precisa de recomendação:
 * baixar sempre pode. Elevar sem recomendação é recusado pelo serviço, com
 * código próprio, e não por validação de forma.
 */
export const SetConfiguredLevelRequestSchema = z.strictObject({
  accountId: UuidV4Schema,
  targetLevel: AutonomyLevelValueSchema,
  recommendationId: UuidV4Schema.nullable(),
  reason: z.string().trim().min(10).max(1_000),
});
export type SetConfiguredLevelRequest = z.infer<
  typeof SetConfiguredLevelRequestSchema
>;

export const AutonomyDashboardSchema = z.strictObject({
  state: AutonomyStateSchema,
  latestEvaluation: AutonomyEvaluationSchema.nullable(),
  /** Motivos operacionais que seguram o modo efetivo, com nome. */
  operationalBlockers: z.array(z.string().min(1)),
  nextEligibleAt: IsoUtcDateTimeSchema.nullable(),
});
export type AutonomyDashboardDto = z.infer<typeof AutonomyDashboardSchema>;
